/**
 * Recovery codes: single-use codes that open two-factor authentication when the phone
 * with the authenticator app is lost (2026-10-03, THE_LIST Tier 2 #14).
 *
 * Supabase returns a set once, in canonical form (lowercase, no separators),
 * and never again. They are shown in blocks of four so they can be read and
 * typed; the server ignores case, spaces and dashes when one is typed back.
 * A set counts as a second factor on its own, so it is removed before the
 * authenticator when two-factor authentication is turned off: otherwise the sign-in
 * would keep asking for a code the creator no longer expects to need.
 */

/** 'k4m9x7qp2ab8ht3z' → 'k4m9-x7qp-2ab8-ht3z'. */
export function formatRecoveryCode(code: string): string {
  return (code.replace(/[^0-9a-z]/gi, '').toLowerCase().match(/.{1,4}/g) ?? []).join('-');
}

/** The text a creator keeps: which account, how the codes work, the codes. */
export function recoveryCodesText(codes: string[], email: string | null | undefined, madeOn: string): string {
  return [
    `Contruly recovery codes${email ? ` for ${email}` : ''}`,
    `Made ${madeOn}. Each code works once, when you cannot use your authenticator app.`,
    '',
    ...codes.map(formatRecoveryCode),
    '',
  ].join('\n');
}

/** A failed recovery-code call, in plain words. */
export function recoveryCodeErrorMessage(error: unknown): string {
  const e = error as { code?: string; message?: string } | null;
  switch (e?.code) {
    case 'mfa_verification_failed':
      return 'That code did not work. Each code works once: try another one from your list.';
    case 'mfa_recovery_codes_locked':
      return 'Too many tries. Wait a few minutes, then try again.';
    case 'mfa_recovery_codes_enroll_not_enabled':
    case 'mfa_recovery_codes_verify_not_enabled':
      return 'Recovery codes are not switched on for Contruly yet.';
    case 'insufficient_aal':
      return 'Sign in with your authenticator app first, then try again.';
  }
  return e?.message || 'The recovery codes did not work. Try again.';
}
